import {EndpointBuilder} from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {createApi} from '@reduxjs/toolkit/query/react';
import {createAuthEndpoint} from './endpoints/auth.endpoint';
import {createAwardsEndpoint} from './endpoints/awards.endpoint';
import {createLivestreamEndpoint} from './endpoints/livestream.endpoint';
import {createSubscriptionEndpoint} from './endpoints/subscription.endpoint';
import {createUmbracoEndpoint} from './endpoints/umbraco.endpoint';
import {createVimeoEndpoint} from './endpoints/vimeo.endpoint';
import {createByteplusEndpoint} from './endpoints/byteplus.endpoint'; // Added import for createByteplusEndpoint
import {traceBaseQueryFn} from './traceBaseQuery';
import {authGuardBaseQuery} from './authGuardBaseQuery';
import {createExamEndpoint} from './endpoints/exam.endpoint';
import {createMemberEndpoint} from './endpoints/members.endpoint';
import {createMemberDataEndpoint} from './endpoints/memberDataEndpoint/memberData.endpoint';
import {createChannelEndpoint} from './endpoints/channel.endpoint';
import {createProxyEndpoint} from './endpoints/proxy.endpoint';
import {createQuizzSurveyEndpoint} from './endpoints/quizzSurveyEndpoint/quizzSurvey.endpoint';
import {createPremiumPreviewEndpoint} from './endpoints/memberDataEndpoint/premiumPreview.endpoint';
import {createSessionsEndpoint} from './endpoints/sessions.endpoint';
import {createVodacomEndpoint} from './endpoints/vodacom.endpoint';
import {createConsentEndpoint} from './endpoints/consent.endpoint';

export type TraceApiEndpointBuilder = EndpointBuilder<
  typeof authGuardBaseQuery,
  never,
  'traceApi'
>;

// Define a service using a base URL and expected endpoints
export const traceApi = createApi({
  reducerPath: 'traceApi',
  baseQuery: authGuardBaseQuery,
  endpoints: builder => ({
    ...createLivestreamEndpoint(builder),
    ...createVimeoEndpoint(builder),
    ...createByteplusEndpoint(builder),
    ...createUmbracoEndpoint(builder),
    ...createAuthEndpoint(builder),
    ...createSubscriptionEndpoint(builder),
    ...createAwardsEndpoint(builder),
    ...createExamEndpoint(builder),
    ...createMemberEndpoint(builder),
    ...createMemberDataEndpoint(builder),
    ...createChannelEndpoint(builder),
    ...createProxyEndpoint(builder),
    ...createQuizzSurveyEndpoint(builder),
    ...createPremiumPreviewEndpoint(builder),
    ...createSessionsEndpoint(builder),
    ...createVodacomEndpoint(builder),
    ...createConsentEndpoint(builder),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetEventByIdQuery,
  useGetEventVideoQuery,
  useLazyGetEventVideoQuery,
  useLazyGetVimeoVideoQuery,
  useLazyCreateMemberQuery,
  useLazyTraceSignUpQuery,
  useLazyRemoveMemberQuery,
  useLazyGetUserSubscriptionQuery,
  useLazyUpdateUserSubscriptionQuery,
  useLazyGetAndroidAccessTokenQuery,
  useLazyPutUmbracoMemberQuery,
  useLazyGetPublicVimeoVideoQuery,
  useLazyAwardsVotingQuery,
  useLazyComputeExamProgressQuery,
  useLazyCheckMemberEmailExistQuery,
  useLazyGetMemberByUsernameQuery,
  useLazyUpdateMemberQuery,
  // member saved
  useLazyCreateMemberSavedQuery,
  useLazyDeleteMemberSavedQuery,
  useLazyGetAllMemberSavedQuery,
  useLazyCountSavedByIdQuery,
  useLazyDeleteMemberSavedByContentIdQuery,
  // member watched,
  useLazyGetAllMemberWatchedQuery,
  useLazyUpdateMemberWatchedQuery,
  // member search
  useLazyGetAllMemberSearchQuery,
  useLazyDeleteMemberSearchQuery,
  useLazyCreateMemberSearchQuery,
  // member notify
  useLazyGetAllMemberNotifyQuery,
  useLazyUpdateMemberNotifyQuery,
  useLazyDeleteMemberNotifyQuery,
  useLazyGetMemberNotifyByIdQuery,
  // member Message
  useLazyGetAllMemberMessageQuery,
  useLazyUpdateMemberMessageQuery,
  useLazyDeleteMemberMessageQuery,
  useLazyGetMessageByIdQuery,
  // member personality
  useLazyGetAllMemberPersonalityTypeQuery,
  useLazyUpdateMemberPersonalityTypeQuery,
  // unitProgression
  useLazyGetMemberUnitProgressionByIdQuery,
  useUpdateMemberUnitProgressionMutation,
  useLazyGetAllUnitProgressionQuery,
  // course test
  useLazyGetMemberCourseTestProgressionByIdQuery,
  useLazyUpdateMemberCourseTestProgressionQuery,
  // course status
  useLazyGetAllCourseStatusQuery,
  useLazyUpdateMemberCourseStatusQuery,
  useLazyGetCourseStatusByIdQuery,
  // member Downloaded
  useLazyGetAllMemberDownloadedQuery,
  useLazyDeleteMemberDownloadedQuery,
  useLazyUpdateMemberDownloadedQuery,
  // member opportunity
  useLazyGetAllMemberOpportunityQuery,
  useLazyGetMemberOpportunityByIdQuery,
  useLazyUpdateMemberOpportunityQuery,
  useLazyDeleteMemberOpportunityQuery,
  // obtained-job
  useLazyGetAllMemberObtainedJobQuery,
  //
  useLazyGetCheckoutSessionQuery,
  useGetCheckoutSessionQuery,
  // vimeo
  useLazyGetVimeoPlayVideosQuery,
  useGetVimeoPlayVideosQuery,
  useLazyGetVimeoEventQuery,
  // byteplus
  useGetByteplusVideoQuery,
  useLazyGetByteplusVideoQuery,
  // promo code
  useLazyGetPromocodeQuery,
  useLazyPostPromocodeQuery,
  useLazyVerifyPromocodeQuery,
  // channel program
  useLazyChannelProgramQuery,
  // Proxy
  useLazyProxyQuery,
  useLazyExtendUrlQuery,
  // subscription
  useLazyGetSubscriptionProductsQuery,
  useCancelStripeSubscriptionMutation,

  // Quizz survey,
  useLazyGetQuizzSurveyProgressQuery,
  useLazyPostQuizzSurveyQuery,

  // auth
  useTvAuthCodeMutation,
  useResetPasswordMutation,

  // Premium view
  useLazyGetPremiumPreviewQuery,
  useAddPremiumPreviewMutation,

  // Sessions
  useRegisterSessionMutation,
  useLazyGetAllSessionsQuery,
  useHeartbeatSessionMutation,
  useRevokeSessionMutation,
  useRevokeAllSessionsMutation,
  useGetAllSessionsQuery,

  // Vodacom
  useVodacomCheckMutation,
  useVodacomLoginMutation,
  useVodacomRefreshMutation,
  useVodacomRequestOtpMutation,
  useVodacomSubmitOtpMutation,

  // Consent
  useSaveConsentMutation,
} = traceApi;
